import {
  CHANGE_AUDIENCE_LABELS,
  changelogFor,
  type ChangeAudience,
  type ChangeKind,
} from '@simpleacs/shared';
import { Card, EmptyState, PageHeader, Pill } from '../components/ui';
import { useSession } from '../lib/session';

const KIND_TONE: Record<ChangeKind, 'primary' | 'success' | 'warn'> = {
  major: 'primary',
  minor: 'success',
  fix: 'warn',
};

const KIND_LABEL: Record<ChangeKind, string> = {
  major: 'Major release',
  minor: 'Minor feature',
  fix: 'Fix',
};

/**
 * A bullet is tagged only when it is NOT the system-wide kind.
 *
 * The tag answers "why is this here when it is not on everybody's page", so
 * putting one on every bullet would answer a question nobody asked and bury
 * the two that matter. It also means a viewer — who by construction sees
 * nothing but `system` — reads a page with no tags on it at all, which is the
 * correct amount of chrome for a reader with one audience.
 */
function AudienceTag({ audience }: { audience: ChangeAudience }) {
  if (audience === 'system') return null;
  return (
    <span className="ml-1 whitespace-nowrap rounded border border-line bg-surface-2 px-1.5 py-0.5 align-middle text-[10px] font-medium uppercase tracking-wide text-faint">
      {CHANGE_AUDIENCE_LABELS[audience]}
    </span>
  );
}

/** The public version history. Linked from the footer's version tag. */
export function ChangelogPage() {
  const { session } = useSession();
  /**
   * Fail closed. This page renders inside the authenticated shell, so a session
   * is always present in practice — but if it ever renders without one, the
   * reader gets the FLOOR of the role ladder rather than everything.
   */
  const role = session?.user.role ?? 'org_viewer';
  const entries = changelogFor(role);

  return (
    <>
      <PageHeader
        title="Changelog"
        description="What changed in each release, for what you can do here. Versioning: v(x) major · v(x.x) minor · v(x.x.x) fix."
      />

      <div className="space-y-5">
        {entries.length === 0 ? (
          /* Only reachable for a release written entirely for a wider audience
             than the reader — an empty page is still better than release cards
             with nothing under them. */
          <Card>
            <EmptyState
              title="Nothing to show yet"
              description="No release notes apply to what you can do on this platform."
            />
          </Card>
        ) : (
          entries.map((entry) => (
            <Card key={entry.version}>
              <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3.5">
                <span className="font-mono text-lg font-semibold text-ink">v{entry.version}</span>
                <Pill state={false} tone={KIND_TONE[entry.kind]}>{KIND_LABEL[entry.kind]}</Pill>
                <span className="text-sm text-muted">{entry.title}</span>
                <span className="ml-auto text-xs text-faint">{entry.date}</span>
              </div>
              <ul className="space-y-2 px-5 py-4">
                {entry.changes.map((change) => (
                  /* Keyed by the prose rather than by index: the list a reader
                     gets is a FILTERED one, so an index is a position in their
                     view and not an identity — two roles would have different
                     bullets under the same key. */
                  <li key={change.text} className="flex gap-2 text-sm text-muted">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary/60" />
                    <span>
                      {change.text}
                      <AudienceTag audience={change.audience} />
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ))
        )}
      </div>
    </>
  );
}
